import { FormEvent, useEffect, useState } from 'react';
import { NavLink, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from '@/hooks';
import { useBranding } from '@/context/BrandingContext';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Toggle from '@/components/ui/Toggle';
import { applicationSettingsService, ApplicationSettings, ApplicationSettingsPatch } from '@/services/applicationSettingsService';
import { settingsSections, SettingsFieldKey, SettingsSection } from './applicationSections';
import HarvestingSettings from './HarvestingSettings';

const QUERY_KEY = ['application-settings'] as const;
type Draft = Partial<Record<SettingsFieldKey, string | number | boolean>>;

const Application = () => {
  const { '*': sectionId } = useParams();
  const { t } = useTranslation();
  const copy = t.applicationSettings.sectionsUi;
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const section = settingsSections.find((item) => item.id === sectionId);

  useEffect(() => {
    setDirty(false);
  }, [sectionId]);

  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (dirty) { event.preventDefault(); event.returnValue = ''; }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (!section && sectionId !== 'security') return <Navigate to="/settings/application/branding" replace />;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <h1 className="mb-4 text-2xl font-bold text-gray-900">{t.applicationSettings.title}</h1>
      <div className="flex flex-col gap-4 lg:flex-row">
        <aside className="w-full shrink-0 lg:w-64">
          <nav aria-label={t.applicationSettings.title} className="flex gap-4 overflow-x-auto border border-gray-200 bg-white p-3 lg:block lg:space-y-4">
            {(['administration', 'android', 'security'] as const).map((domain) => (
              <div key={domain}>
                <h2 className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{copy[domain]}</h2>
                <div className="flex lg:block">
                  {(domain === 'security' ? [{ id: 'security' }] : settingsSections.filter((item) => item.domain === domain)).map((item) => (
                    <NavLink key={item.id} to={`/settings/application/${item.id}`}
                      onClick={(event) => {
                        if (saving || (item.id !== sectionId && dirty && !window.confirm(copy.discard))) event.preventDefault();
                      }}
                      className={({ isActive }) => `block whitespace-nowrap px-3 py-2 text-sm font-medium ${isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700 hover:bg-gray-50'}`}>
                      {item.id === 'security' ? t.applicationSettings.vodHarvesting.title : copy.sections[item.id as keyof typeof copy.sections]}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>
        <section className="min-w-0 flex-1 rounded border border-gray-200 bg-white p-5 sm:p-6">
          {section ? <SettingsSectionForm key={section.id} section={section} onDirty={setDirty} onSaving={setSaving} /> : <HarvestingSettings onDirty={setDirty} onSaving={setSaving} />}
        </section>
      </div>
    </div>
  );
};

function SettingsSectionForm({ section, onDirty, onSaving }: { section: SettingsSection; onDirty: (dirty: boolean) => void; onSaving: (saving: boolean) => void }) {
  const { t } = useTranslation();
  const copy = t.applicationSettings;
  const ui = copy.sectionsUi;
  const queryClient = useQueryClient();
  const { refreshBranding } = useBranding();
  const [draft, setDraft] = useState<Draft>({});
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: applicationSettingsService.getSettings });
  const mutation = useMutation({
    mutationFn: applicationSettingsService.updateSettings,
    onSuccess: (settings: ApplicationSettings) => {
      queryClient.setQueryData(QUERY_KEY, settings);
      setDraft({});
      onDirty(false);
      if (section.domain === 'administration') void refreshBranding();
      toast.success(copy.saved);
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : copy.saveError),
  });
  useEffect(() => { onSaving(mutation.isPending); return () => onSaving(false); }, [mutation.isPending, onSaving]);
  const update = (key: SettingsFieldKey, value: string | number | boolean) => {
    setDraft((current) => ({ ...current, [key]: value }));
    onDirty(true);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    // Field controls constrain values; send only fields belonging to the active section.
    const changes = Object.fromEntries(section.fields.filter(({ key }) => key in draft).map(({ key }) => [key, draft[key]]));
    const patch: ApplicationSettingsPatch = section.domain === 'administration' ? { administration: changes } : { android: changes };
    mutation.mutate(patch);
  };

  if (query.isPending) return <p role="status">{ui.loading}</p>;
  if (query.isError || !query.data) return <div role="alert"><p className="mb-3 text-red-600">{copy.loadError}</p><Button onClick={() => query.refetch()}>{ui.retry}</Button></div>;
  const source = query.data[section.domain];
  if (!source) return <p role="alert">{ui.missingAdmin}</p>;
  const values = source as unknown as Record<string, string | number | boolean | null>;
  const optionsFor = (key: SettingsFieldKey) => key === 'themeId'
    ? [{ value: '', label: ui.noTheme }, ...query.data.themes.map((theme) => ({ value: theme.id, label: theme.id }))]
    : key === 'layout' ? [
      { value: 'DEFAULT', label: copy.layouts.default },
      { value: 'VERTICAL_MENU', label: copy.layouts.verticalMenu },
      { value: 'HOSPITALITY', label: copy.layouts.hospitality },
      { value: 'MODERN', label: copy.layouts.modern },
    ] : [{ value: 'DEFAULT', label: ui.defaultValue }, { value: 'TOP_CENTER', label: ui.topCenter }];

  return (
    <form onSubmit={submit}>
      <h2 className="text-xl font-semibold text-gray-900">{ui.sections[section.id as keyof typeof ui.sections]}</h2>
      <p className="mt-1 text-sm text-gray-500">{section.domain === 'administration' ? ui.adminDescription : ui.androidDescription}</p>
      <fieldset disabled={mutation.isPending} className="mt-6 grid gap-5 sm:grid-cols-2">
        {section.fields.map(({ key, kind }) => {
          const label = ui.fields[key];
          const value = draft[key] ?? (key === 'themeId' ? query.data.android.theme?.id ?? '' : values[key]);
          if (kind === 'toggle') {
            const defaultEnabled = section.domain === 'android' && (key === 'changePasswordEnabled' || key === 'fullScreenEnterActsAsBack' || key.endsWith('ShowPremiere') || key.endsWith('ShowRecentlyAdded'));
            return <Toggle key={key} label={label} helperText={value == null ? ui.defaultValue : undefined} checked={value == null ? defaultEnabled : value === true} onChange={(checked) => update(key, checked)} />;
          }
          if (kind === 'select') return <Select key={key} aria-label={label} label={label} value={String(value ?? 'DEFAULT')} options={optionsFor(key)} onChange={(event) => update(key, event.target.value)} />;
          return <Input key={key} aria-label={label} label={label} type={kind} value={value == null ? '' : String(value)}
            min={kind === 'number' ? 0 : undefined} max={kind === 'number' ? 2147483647 : undefined} step={kind === 'number' ? 1 : undefined}
            required={kind === 'number' && key in draft}
            placeholder={value == null ? ui.defaultValue : undefined}
            onChange={(event) => update(key, kind === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)} />;
        })}
      </fieldset>
      <div className="mt-8 flex items-center gap-4 border-t border-gray-200 pt-5">
        <Button type="submit" loading={mutation.isPending} disabled={!Object.keys(draft).length || mutation.isPending}>{copy.save}</Button>
        {Object.keys(draft).length > 0 && <span className="text-sm text-amber-700">{ui.unsaved}</span>}
      </div>
      <p className="mt-4 text-xs text-gray-400">{ui.recordId}: {source.id}</p>
    </form>
  );
}

export default Application;
